import React from "react";
import styles from "./Form.module.css";

import {
  useGetDistrictsQuery,
  useGetCountriesSelectedQuery,
  useGetAreaDistrictsQuery,
} from "../../redux/api/regionApi";

import {
  useGetcategoriesDropDownQuery,
  useGetPcategoriesDropDownQuery,
} from "../../redux/api/classifiedApi";

const AddClassiedPostCommonFields = ({
  formData,
  handleChange,
  errors = {},
  classifiedType,
  classified_id,
}) => {
  /*
   * --------------------------------------------------
   * Countries
   * --------------------------------------------------
   */

  const {
    data: countries = [],
    error: countryError,
    isSuccess: countrySuccess,
  } = useGetCountriesSelectedQuery({});

  /*
   * --------------------------------------------------
   * Property lease type
   * --------------------------------------------------
   */

  const {
    data: leaseTypeData = [],
    error: leaseTypeError,
    isSuccess: leaseTypeSuccess,
  } = useGetPcategoriesDropDownQuery({
    category_id: classified_id,
  });

  /*
   * --------------------------------------------------
   * Career Level / Property Building Type
   *
   * jobs:
   *   career_level
   *
   * property:
   *   property_for_rent
   * --------------------------------------------------
   */

  const {
    data: typeData = [],
    error: typeError,
    isSuccess: typeSuccess,
  } = useGetcategoriesDropDownQuery({
    idName: classifiedType === "jobs" ? "career_level" : "property_for_rent",

    category_id: classified_id,
  });

  /*
   * --------------------------------------------------
   * Property Features
   * --------------------------------------------------
   */

  const {
    data: propertiesFeaturesData = [],
    error: propertiesFeaturesError,
    isSuccess: propertiesFeaturesSuccess,
  } = useGetcategoriesDropDownQuery({
    idName: "properities",
    category_id: classified_id,
  });

  /*
   * --------------------------------------------------
   * District
   * --------------------------------------------------
   */

  const {
    data: districts = [],
    error: districtError,
    isSuccess: districtSuccess,
  } = useGetDistrictsQuery({
    country_id: formData.country_id || "KW",
    searchText: "",
  });

  /*
   * --------------------------------------------------
   * Area
   * --------------------------------------------------
   */

  const {
    data: areas = [],
    error: areaError,
    isSuccess: areaSuccess,
  } = useGetAreaDistrictsQuery(
    {
      district_id: formData.district_id,
      searchText: "",
    },
    {
      skip: !formData.district_id,
    },
  );

  /*
   * --------------------------------------------------
   * Helper for API errors
   * --------------------------------------------------
   */

  const getErrorMessage = (error) => {
    return error?.data?.error || error?.error || null;
  };

  return (
    <>
      {/* ------------------------------------------- */}
      {/* API ERRORS */}
      {/* ------------------------------------------- */}

      {getErrorMessage(countryError) && (
        <span className={styles.error}>{getErrorMessage(countryError)}</span>
      )}

      {getErrorMessage(districtError) && (
        <span className={styles.error}>{getErrorMessage(districtError)}</span>
      )}

      {getErrorMessage(areaError) && (
        <span className={styles.error}>{getErrorMessage(areaError)}</span>
      )}

      {getErrorMessage(leaseTypeError) && (
        <span className={styles.error}>{getErrorMessage(leaseTypeError)}</span>
      )}

      {getErrorMessage(typeError) && (
        <span className={styles.error}>{getErrorMessage(typeError)}</span>
      )}

      {getErrorMessage(propertiesFeaturesError) && (
        <span className={styles.error}>
          {getErrorMessage(propertiesFeaturesError)}
        </span>
      )}

      {/* ------------------------------------------- */}
      {/* PROPERTY - LEASE TYPE */}
      {/* ------------------------------------------- */}

      {classifiedType === "property" && (
        <div className={styles.field}>
          <label htmlFor="leaseType">Select Lease Type</label>

          <select
            id="leaseType"
            name="leaseType"
            value={formData.leaseType || ""}
            onChange={handleChange}
          >
            <option value="">Select Lease Type</option>

            {leaseTypeSuccess &&
              leaseTypeData.map((item) => (
                <option value={item._id} key={item._id}>
                  {item.sub_category_en}
                </option>
              ))}
          </select>

          {errors.leaseType && (
            <span className={styles.error}>{errors.leaseType}</span>
          )}
        </div>
      )}

      {/* ------------------------------------------- */}
      {/* PROPERTY - BUILDING TYPE */}
      {/* JOBS - CAREER LEVEL */}
      {/* ------------------------------------------- */}

      {(classifiedType === "property" || classifiedType === "jobs") && (
        <div className={styles.field}>
          <label
            htmlFor={classifiedType === "jobs" ? "careerLevel" : "buildingType"}
          >
            {classifiedType === "jobs" ? "Career Level" : "Building Type"}
          </label>

          <select
            id={classifiedType === "jobs" ? "careerLevel" : "buildingType"}
            name={classifiedType === "jobs" ? "careerLevel" : "buildingType"}
            value={
              classifiedType === "jobs"
                ? formData.careerLevel || ""
                : formData.buildingType || ""
            }
            onChange={handleChange}
          >
            <option value="">
              {classifiedType === "jobs"
                ? "Select Career Level"
                : "Select Building Type"}
            </option>

            {typeSuccess &&
              typeData.map((item) => (
                <option value={item._id} key={item._id}>
                  {item.value_en}
                </option>
              ))}
          </select>

          {classifiedType === "property" && errors.buildingType && (
            <span className={styles.error}>{errors.buildingType}</span>
          )}

          {classifiedType === "jobs" && errors.careerLevel && (
            <span className={styles.error}>{errors.careerLevel}</span>
          )}
        </div>
      )}

      {/* ------------------------------------------- */}
      {/* COUNTRY */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="country">Country</label>

        <select
          id="country"
          name="country_id"
          value={formData.country_id || "KW"}
          onChange={handleChange}
        >
          <option value="">Select Country</option>

          {countrySuccess &&
            countries.map((country) => (
              <option value={country._id} key={country._id}>
                {country.country_english}
              </option>
            ))}
        </select>

        {errors.country && (
          <span className={styles.error}>{errors.country}</span>
        )}
      </div>

      {/* ------------------------------------------- */}
      {/* DISTRICT */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="district">District</label>

        <select
          id="district"
          name="district_id"
          value={formData.district_id || ""}
          onChange={handleChange}
        >
          <option value="">Select District</option>

          {districtSuccess &&
            districts.map((district) => (
              <option value={district._id} key={district._id}>
                {district.district_en}
              </option>
            ))}
        </select>

        {errors.district && (
          <span className={styles.error}>{errors.district}</span>
        )}
      </div>

      {/* ------------------------------------------- */}
      {/* AREA */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="area">Area</label>

        <select
          id="area"
          name="area_id"
          value={formData.area_id || ""}
          onChange={handleChange}
          disabled={!formData.district_id}
        >
          <option value="">Select Area</option>

          {areaSuccess &&
            areas.map((area) => (
              <option value={area._id} key={area._id}>
                {area.area_en}
              </option>
            ))}
        </select>

        {errors.area && <span className={styles.error}>{errors.area}</span>}
      </div>

      {/* ------------------------------------------- */}
      {/* PROPERTY FIELDS */}
      {/* ------------------------------------------- */}

      {classifiedType === "property" && (
        <>
          {/* STREET */}

          <div className={styles.field}>
            <label htmlFor="street">Street</label>

            <input
              type="text"
              id="street"
              name="street"
              value={formData.street || ""}
              onChange={handleChange}
            />

            {errors.street && (
              <span className={styles.error}>{errors.street}</span>
            )}
          </div>

          {/* BUILDING NO */}

          <div className={styles.field}>
            <label htmlFor="buildingNo">Building No</label>

            <input
              type="text"
              id="buildingNo"
              name="buildingNo"
              value={formData.buildingNo || ""}
              onChange={handleChange}
            />

            {errors.buildingNo && (
              <span className={styles.error}>{errors.buildingNo}</span>
            )}
          </div>

          {/* FLOOR */}

          <div className={styles.field}>
            <label htmlFor="floor">Floor</label>

            <input
              type="text"
              id="floor"
              name="floor"
              value={formData.floor || ""}
              onChange={handleChange}
            />

            {errors.floor && (
              <span className={styles.error}>{errors.floor}</span>
            )}
          </div>

          {/* FLAT NO */}

          <div className={styles.field}>
            <label htmlFor="flatNo">Flat No</label>

            <input
              type="text"
              id="flatNo"
              name="flatNo"
              value={formData.flatNo || ""}
              onChange={handleChange}
            />

            {errors.flatNo && (
              <span className={styles.error}>{errors.flatNo}</span>
            )}
          </div>

          {/* BLOCK NO */}

          <div className={styles.field}>
            <label htmlFor="blockNo">Block No</label>

            <input
              type="text"
              id="blockNo"
              name="blockNo"
              value={formData.blockNo || ""}
              onChange={handleChange}
            />

            {errors.blockNo && (
              <span className={styles.error}>{errors.blockNo}</span>
            )}
          </div>

          {/* ROOMS */}

          <div className={styles.field}>
            <label htmlFor="rooms">Rooms</label>

            <input
              type="text"
              id="rooms"
              name="rooms"
              value={formData.rooms || ""}
              onChange={handleChange}
            />

            {errors.rooms && (
              <span className={styles.error}>{errors.rooms}</span>
            )}
          </div>

          {/* LIVING ROOMS */}

          <div className={styles.field}>
            <label htmlFor="livingRooms">Living Rooms</label>

            <input
              type="text"
              id="livingRooms"
              name="livingRooms"
              value={formData.livingRooms || ""}
              onChange={handleChange}
            />

            {errors.livingRooms && (
              <span className={styles.error}>{errors.livingRooms}</span>
            )}
          </div>

          {/* BATHROOMS */}

          <div className={styles.field}>
            <label htmlFor="bathrooms">Bathrooms</label>

            <input
              type="text"
              id="bathrooms"
              name="bathrooms"
              value={formData.bathrooms || ""}
              onChange={handleChange}
            />

            {errors.bathrooms && (
              <span className={styles.error}>{errors.bathrooms}</span>
            )}
          </div>

          {/* BUILDING AGE */}

          <div className={styles.field}>
            <label htmlFor="buildingAge">Building Age</label>

            <input
              type="text"
              id="buildingAge"
              name="buildingAge"
              value={formData.buildingAge || ""}
              onChange={handleChange}
            />

            {errors.buildingAge && (
              <span className={styles.error}>{errors.buildingAge}</span>
            )}
          </div>
        </>
      )}

      {/* ------------------------------------------- */}
      {/* ADDRESS */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="address">Other Address</label>

        <input
          type="text"
          id="address"
          name="address"
          value={formData.address || ""}
          onChange={handleChange}
        />

        {errors.address && (
          <span className={styles.error}>{errors.address}</span>
        )}
      </div>

      {/* ------------------------------------------- */}
      {/* TITLE */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="title">Title</label>

        <input
          type="text"
          id="title"
          name="title"
          value={formData.title || ""}
          onChange={handleChange}
        />

        {errors.title && <span className={styles.error}>{errors.title}</span>}
      </div>

      {/* ------------------------------------------- */}
      {/* MOBILE */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="mobile">Mobile</label>

        <input
          type="text"
          id="mobile"
          name="mobile"
          value={formData.mobile || ""}
          onChange={handleChange}
        />

        {errors.mobile && <span className={styles.error}>{errors.mobile}</span>}
      </div>

      {/* ------------------------------------------- */}
      {/* PRICE */}
      {/* ------------------------------------------- */}

      <div className={styles.field}>
        <label htmlFor="price">Price</label>

        <input
          type="text"
          id="price"
          name="price"
          value={formData.price ?? ""}
          onChange={handleChange}
        />

        {errors.price && <span className={styles.error}>{errors.price}</span>}
      </div>

      {/* ------------------------------------------- */}
      {/* PROPERTY PARKING */}
      {/* ------------------------------------------- */}

      {classifiedType === "property" && (
        <div className={styles.fieldFull}>
          <div>
            <label>
              <input
                type="checkbox"
                name="bikeParking"
                checked={Boolean(formData.bikeParking)}
                onChange={handleChange}
              />

              <span
                style={{
                  marginLeft: "5px",
                }}
              >
                Bike Parking
              </span>
            </label>

            <label>
              <input
                type="checkbox"
                name="carParking"
                checked={Boolean(formData.carParking)}
                onChange={handleChange}
                style={{
                  marginLeft: "5px",
                }}
              />

              <span
                style={{
                  marginLeft: "5px",
                }}
              >
                Car Parking
              </span>
            </label>
          </div>

          {errors.bikeParking && (
            <span className={styles.error}>{errors.bikeParking}</span>
          )}

          {errors.carParking && (
            <span className={styles.error}>{errors.carParking}</span>
          )}
        </div>
      )}

      {/* ------------------------------------------- */}
      {/* JOBS - CAREER LEVEL */}
      {/* ------------------------------------------- */}

      {/* Already rendered above with the
          jobs/property type dropdown */}

      {/* ------------------------------------------- */}
      {/* BUY & SELL */}
      {/* ------------------------------------------- */}

      {classifiedType === "buy & sell" && (
        <div className={styles.fieldFull}>
          <label htmlFor="oldItem">
            <input
              type="radio"
              id="oldItem"
              name="isItOldNotNew"
              value="old"
              checked={formData.isItOldNotNew === "old"}
              onChange={handleChange}
            />{" "}
            Old
          </label>

          <label
            htmlFor="newItem"
            style={{
              marginLeft: "15px",
            }}
          >
            <input
              type="radio"
              id="newItem"
              name="isItOldNotNew"
              value="new"
              checked={formData.isItOldNotNew === "new"}
              onChange={handleChange}
            />{" "}
            New
          </label>

          {errors.isItOldNotNew && (
            <span className={styles.error}>{errors.isItOldNotNew}</span>
          )}
        </div>
      )}

      {/* ------------------------------------------- */}
      {/* DESCRIPTION */}
      {/* ------------------------------------------- */}

      <div className={styles.fieldFull}>
        <label htmlFor="description">Description</label>

        <textarea
          id="description"
          name="description"
          value={formData.description || ""}
          onChange={handleChange}
        />
      </div>
    </>
  );
};

export default AddClassiedPostCommonFields;
